import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { SubscriptionPlan } from '../../utils/subscription';

interface PlanLimits {
  free: number;
  standard: number;
  max: number;
}

interface PlanUpgradeCTAProps {
  /** 現在のプラン */
  currentPlan: number;
  /** 現在の利用量（文字数など） */
  currentValue: number;
  /** プラン別上限値（subscription.ts の MEMO_LIMITS など） */
  limits: PlanLimits;
  /** 単位の表示文字（"字"・"件"・"枚"など） */
  unit: string;
  /** 動詞の連用形（"書け"・"作れ"・"入れられ"など）。"〜ます" が後続する。デフォルト "書け" */
  verb?: string;
  /**
   * 上限超過時に「○倍」と倍率で見せるか。デフォルト true。
   * 上限値の比が半端な場合（例: 3→20→100）は false にして実数表示にする。
   */
  showMultiplier?: boolean;
  /** プラン誘導先 */
  upgradePath?: string;
  /** 表示しきい値（デフォルト 0.8 = 80%）。これ未満は非表示 */
  threshold?: number;
}

/**
 * 上限到達時のプラン誘導 CTA。
 * FREE / STANDARD のときだけ表示。MAX 以上は常に非表示。
 * 文字数だけでなく、件数制限など上限のあるあらゆる文脈で再利用可能。
 *
 * 表示遷移：
 *   < threshold(80%)         : 非表示
 *   threshold〜100%           : 控えめ表示（次プランの上限を案内）
 *   100% 到達／超過           : 強調表示（強い CTA 文言）
 */
const PlanUpgradeCTA: React.FC<PlanUpgradeCTAProps> = ({
  currentPlan,
  currentValue,
  limits,
  unit,
  verb = '書け',
  showMultiplier = true,
  upgradePath = '/pricing',
  threshold = 0.8,
}) => {
  // MAX 以上には出さない
  if (currentPlan >= SubscriptionPlan.MAX) return null;

  const isFree = currentPlan < SubscriptionPlan.PRO;
  const myMax = isFree ? limits.free : limits.standard;
  const ratio = myMax > 0 ? currentValue / myMax : 0;

  // しきい値未満は非表示
  if (ratio < threshold) return null;

  const isOver = currentValue >= myMax;

  let message: React.ReactNode;
  if (isFree) {
    if (isOver) {
      if (showMultiplier) {
        const standardMul = Math.round(limits.standard / limits.free);
        const maxMul = Math.round(limits.max / limits.free);
        message = (
          <>
            <strong>STANDARDで{standardMul}倍</strong>、<strong>MAXで{maxMul}倍</strong>{verb}ます
          </>
        );
      } else {
        message = (
          <>
            <strong>STANDARDで{limits.standard.toLocaleString()}{unit}</strong>、
            <strong>MAXで{limits.max.toLocaleString()}{unit}</strong>{verb}ます
          </>
        );
      }
    } else {
      message = (
        <>
          STANDARDなら <strong>{limits.standard.toLocaleString()}{unit}</strong> まで{verb}ます
        </>
      );
    }
  } else {
    // STANDARD
    if (isOver) {
      if (showMultiplier) {
        const mul = Math.round(limits.max / limits.standard);
        message = (
          <>
            MAXなら <strong>{mul}倍</strong> の <strong>{limits.max.toLocaleString()}{unit}</strong> {verb}ます
          </>
        );
      } else {
        message = (
          <>
            MAXなら <strong>{limits.max.toLocaleString()}{unit}</strong> まで{verb}ます
          </>
        );
      }
    } else {
      message = (
        <>
          MAXなら <strong>{limits.max.toLocaleString()}{unit}</strong> まで{verb}ます
        </>
      );
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        bgcolor: isOver ? 'rgba(217,255,0,0.06)' : 'rgba(255,255,255,0.03)',
        border: '1px solid',
        borderColor: isOver ? 'rgba(217,255,0,0.25)' : 'rgba(255,255,255,0.08)',
      }}
    >
      <AutoAwesomeIcon
        sx={{
          fontSize: '0.95rem',
          color: isOver ? 'primary.main' : 'text.secondary',
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: isOver ? 'text.primary' : 'text.secondary',
          flexGrow: 1,
          lineHeight: 1.4,
          fontSize: '0.72rem',
        }}
      >
        {message}
      </Typography>
      <Link
        component="button"
        type="button"
        onClick={() => window.open(upgradePath, '_blank', 'noopener,noreferrer')}
        sx={{
          fontSize: '0.72rem',
          color: 'primary.main',
          fontWeight: isOver ? 600 : 500,
          whiteSpace: 'nowrap',
          textDecoration: 'none',
          flexShrink: 0,
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        プランを見る →
      </Link>
    </Box>
  );
};

export default PlanUpgradeCTA;
